import useSWR from 'swr'

import { ERROR_MESSAGES } from '@/constants/errorMessages'

import { CacheKey } from './cacheKey'
import { ApiError } from './error'

export type UseQueryOption<Data> = {
  revalidateIfStale?: boolean
  revalidateOnFocus?: boolean
  revalidateOnReconnect?: boolean
  revalidateOnMount?: boolean
  refreshInterval?: number
  onSuccess?: (data: Data) => void
  onError?: (error: ApiError) => void
}

type UseQueryReturn<Data, Suspense extends boolean = false> = {
  data: Suspense extends true ? Data : Data | undefined
  error: ApiError | undefined
  mutate: (data?: Data) => void
  isLoading: boolean
  isValidating: boolean
}

type QueryFn<Data> = () => Promise<Data>

const getApiError = (error: unknown): ApiError => {
  if (error instanceof ApiError) {
    return error
  }
  return new ApiError(ERROR_MESSAGES.defaultApiError)
}

export const useQuery = <Data>(
  key: CacheKey | null,
  queryFn: QueryFn<Data>,
  option?: UseQueryOption<Data>,
): UseQueryReturn<Data> => {
  const { data, error, mutate, isLoading, isValidating } = useSWR<Data>(
    key,
    () => queryFn(),
    {
      ...option,
      onError: error => {
        option?.onError?.(getApiError(error))
      },
    },
  )
  return {
    data,
    mutate,
    error,
    isLoading,
    isValidating,
  }
}

export const useQuerySuspense = <Data>(
  key: CacheKey,
  queryFn: QueryFn<Data>,
  option?: UseQueryOption<Data>,
): UseQueryReturn<Data, true> => {
  const { data, error, mutate, isLoading, isValidating } = useSWR<Data>(
    key,
    () => queryFn(),
    {
      ...option,
      onError: error => {
        option?.onError?.(getApiError(error))
      },
      suspense: true,
    },
  )
  return {
    data: data!,
    error,
    mutate,
    isLoading,
    isValidating,
  }
}
